import { useEffect, useState } from "react";

/**
 * How far under the fold something has to lie before it counts. A scroll
 * position is a fraction on a dense screen while the heights beside it are
 * whole pixels, so the end of a scroll can read as half a pixel short of it.
 */
const TOLERANCE_PX = 1;

/**
 * Whether a box that scrolls has something drawn under its lower edge right
 * now.
 *
 * The box's own closing padding is the last thing in it and draws nothing, so
 * a fold that hides only that hides nothing: a 390 × 844px phone leaves the
 * auto-sync form 14px short of its 16px of padding, every line of it in view.
 */
function hasContentBelowFold(scroller: HTMLElement): boolean {
    const hidden = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
    // jsdom resolves no stylesheet and a box nobody padded answers "0px": both read as none.
    const closingPadding = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
    return hidden > closingPadding + TOLERANCE_PX;
}

/**
 * Says whether `scroller`, a box that scrolls its content, hides something of
 * it under the fold: for a mark that tells the reader the content goes on.
 *
 * macOS draws no scrollbar until something scrolls, and the product's own
 * scrollbars have a see-through track, so where a fold falls in the gap
 * between two blocks nothing on screen says there is more. The auto-sync
 * dialog keeps Save in view whatever its body hides, and an operator there
 * could save without having seen the settings below.
 *
 * It is read again on every scroll, and whenever the box or what it holds
 * changes size with nothing scrolled: a window resized, a switch that opens
 * the rest of a form, a list that loads, a notice that appears. For the
 * second, the box's **first element child is watched as its content**, as
 * `TopScrollbar` watches its target's: hold everything the box scrolls in one
 * child, or content that grows inside a box already at its full height is
 * not seen to.
 *
 * Hand it the element itself, from a callback ref kept in state
 * (`const [body, setBody] = useState<HTMLElement | null>(null)`, then
 * `ref={setBody}`), not a ref object: a dialog mounts its content only while
 * it is open, and an effect keyed on a ref object would not run again then.
 * `null` reads as nothing hidden.
 */
export function useContentBelowFold(scroller: HTMLElement | null): boolean {
    const [below, setBelow] = useState(false);

    useEffect(() => {
        if (!scroller) {
            setBelow(false);
            return;
        }
        const measure = () => setBelow(hasContentBelowFold(scroller));
        measure();
        scroller.addEventListener("scroll", measure, { passive: true });
        const observer = new ResizeObserver(measure);
        observer.observe(scroller);
        if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
        return () => {
            scroller.removeEventListener("scroll", measure);
            observer.disconnect();
        };
    }, [scroller]);

    return below;
}
